import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import {
  INTENT_PROJECT_HISTORY_LIMIT,
  INTENT_PROJECT_HISTORY_LIMIT_STORAGE_KEY,
} from "views/Account/IntentProject/History/Table/constants";

import useIntentProjectOperationFiltersVariables from "../useSearchIntentResourcesOperationsSuggestions/useFiltersVariables";
import { SEARCH_INTENT_RESOURCES_OPERATIONS } from "./gql";
import useIntentProjectOperationsStore from "./store";

const useSearchIntentResourcesOperations = (intentProjectId: string) => {
  const {
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  } = useIntentProjectOperationsStore();
  const [storedRowsPerPage] = useLocalStorage<number>(INTENT_PROJECT_HISTORY_LIMIT_STORAGE_KEY);
  const { fullTextSearch, predicates, filterVariablesLoaded, sortOptionFields } =
    useIntentProjectOperationFiltersVariables();

  const first = storedRowsPerPage || INTENT_PROJECT_HISTORY_LIMIT;

  const paginationResetKey = useMemo(
    () => ({ intentProjectId, fullTextSearch, predicates, sortOptionFields }),
    [intentProjectId, fullTextSearch, predicates, sortOptionFields]
  );

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { data, error, previousData, networkStatus } = usePolledQuery(
    SEARCH_INTENT_RESOURCES_OPERATIONS,
    {
      variables: {
        intentProjectId,
        input: {
          fullTextSearch,
          first,
          after: cursor,
          orderBy: sortOptionFields,
          predicates,
          requestedPage,
        },
      },
      skip: !filterVariablesLoaded,
      pollingGroup: PollingIntervalGroups.SingleEntities,
      notifyOnNetworkStatusChange: true,
    }
  );

  const operations = useMemo(() => {
    const edges =
      data?.intentProject?.searchIntentResourcesOperations?.edges ??
      previousData?.intentProject?.searchIntentResourcesOperations?.edges ??
      [];

    return edges.map((edge) => edge.node);
  }, [data, previousData]);

  const initialLoading =
    (operations.length === 0 && networkStatus === NetworkStatus.loading) || !filterVariablesLoaded;

  const loadingContent =
    !filterVariablesLoaded ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.setVariables;

  const isEmpty = Boolean(data) && !operations.length && !initialLoading;

  return {
    operations,
    initialLoading,
    loadingContent,
    isEmpty,
    error,
    startCursor: data?.intentProject?.searchIntentResourcesOperations?.pageInfo.startCursor,
    endCursor: data?.intentProject?.searchIntentResourcesOperations?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSearchIntentResourcesOperations;
